import Dexie from 'dexie';
import { logger } from '../utils/logger';
import { CONFIG } from './config';
import { ProjectData } from '../hooks/queries/projectTypes';
import { TopicData } from '../hooks/queries/topicTypes';
import { TaskColumnData } from '../hooks/queries/taskColumnTypes';
import { TaskData } from '../hooks/queries/taskTypes';
import { NoteData } from '../hooks/queries/noteTypes';
import { BookmarkData } from '../hooks/queries/bookmarkTypes';
import { ListItemData } from '../hooks/queries/listTypes';
import { CustomerSubscription, Price } from '../hooks/queries/subscriptionTypes';
import { Notification } from '../hooks/queries/notificationTypes';
import { Collaborator, Invitation } from '../hooks/queries/collaboratorTypes';
import { MoodboardItemData } from '../hooks/queries/moodboardTypes';
import { ChatMessageData } from '../hooks/queries/chatTypes';

/**
 * ZentimeDatabase extends Dexie to provide a typed IndexedDB implementation
 * for offline data storage and synchronization.
 */
export class ZentimeDatabase extends Dexie {
  // Table definitions with typings
  projects!: Dexie.Table<ProjectData & { isDeleted?: boolean }, string>;
  topics!: Dexie.Table<TopicData & { isDeleted?: boolean }, string>;
  taskColumns!: Dexie.Table<TaskColumnData & { isDeleted?: boolean }, string>;
  tasks!: Dexie.Table<TaskData & { isDeleted?: boolean }, string>;
  notes!: Dexie.Table<NoteData & { isDeleted?: boolean }, string>;
  bookmarks!: Dexie.Table<BookmarkData & { isDeleted?: boolean }, string>;
  listItems!: Dexie.Table<ListItemData & { isDeleted?: boolean }, string>;
  moodboardItems!: Dexie.Table<MoodboardItemData & { isDeleted?: boolean }, string>;
  chatMessages!: Dexie.Table<ChatMessageData & { isDeleted?: boolean }, string>;
  notifications!: Dexie.Table<Notification & { isDeleted?: boolean }, string>;
  collaborators!: Dexie.Table<Collaborator & { isDeleted?: boolean }, number>;
  invitations!: Dexie.Table<Invitation & { isDeleted?: boolean }, string>;
  customerSubscriptions!: Dexie.Table<CustomerSubscription & { user_id: string }, string>;
  prices!: Dexie.Table<Price, string>;
  offlineOperations!: Dexie.Table<OfflineOperation, string>;
  projectAccess!: Dexie.Table<ProjectAccessCache, string>;
  // Table topic tables
  tableDefinitions!: Dexie.Table<import('./queries/tableTypes').TableDefinitionData & { isDeleted?: boolean }, string>;
  tableColumns!: Dexie.Table<import('./queries/tableTypes').TableColumnData & { isDeleted?: boolean }, string>;
  tableRows!: Dexie.Table<import('./queries/tableTypes').TableRowData & { isDeleted?: boolean }, string>;
  tableCells!: Dexie.Table<import('./queries/tableTypes').TableCellData & { isDeleted?: boolean }, string>;

  constructor() {
    super('ZentimeOffline');
    
    // Handle database upgrade errors
    this.on('blocked', () => {
      logger.warn('Database upgrade blocked - other tabs may have the database open');
    });
    
    this.on('versionchange', () => {
      logger.warn('Database version changed - reloading may be required');
    });
    
    this.version(14).stores({
      // Store projects with indexes for faster queries
      projects: 'id, user_id, archived, updated_at',
      
      // Store topics with indexes for faster queries
      topics: 'id, project_id, topic_type, last_used_at',
      
      // Store task columns with indexes for faster queries
      taskColumns: 'id, topic_id, status, position',
      
      // Store tasks with indexes for faster queries
      tasks: 'id, topic_id, status, deadline, assigned_to, updated_at',
      
      // Store notes with indexes for faster queries
      notes: 'id, topic_id, user_id, updated_at',
      
      // Store bookmarks with indexes for faster queries
      bookmarks: 'id, topic_id, user_id, updated_at',
      
      // Store list items with indexes for faster queries
      listItems: 'id, topic_id, order_index, user_id, updated_at',
      
      // Store moodboard items with indexes for faster queries
      moodboardItems: 'id, topic_id, user_id, updated_at',
      
      // Store chat messages with indexes for faster queries
      chatMessages: 'id, topic_id, user_id, role, created_at, updated_at',
      
      // Store notifications with indexes for faster queries
      notifications: 'id, user_id, is_read, created_at, created_at_ms',

      // Store collaborator data with indexes for faster queries
      collaborators: 'id, project_id, user_id',

      // Store invitation data with indexes for faster queries
      invitations: 'id, project_id, invitee_email, status',
      
      // Store customer subscription data
      customerSubscriptions: 'user_id, subscription_status, subscription_tier, updated_at',
      
      // Store price data
      prices: 'id, active, stripe_price_id, unit_amount',
      
      // Store operations that need to be synchronized when online
      offlineOperations: 'id, table, operation, entity_id, timestamp, synced',
      
      // Store project access permissions for offline support
      projectAccess: 'id, userId, projectId, [userId+projectId], expiresAt',
      
      // Table Topic - store table definitions with indexes for faster queries
      tableDefinitions: 'id, topic_id, updated_at',
      
      // Table Topic - store table columns with indexes for faster queries
      tableColumns: 'id, table_definition_id, order_index, updated_at',
      
      // Table Topic - store table rows with indexes for faster queries
      tableRows: 'id, table_definition_id, order_index, updated_at',
      
      // Table Topic - store table cells with indexes for faster queries
      tableCells: 'id, row_id, column_id, [row_id+column_id], updated_at'
    });

    // Handle database ready event - using Dexie 4.0 event API
    this.on("ready", () => {
      // Log database open success using store logger (since this is database/storage related)
      logger.store('IndexedDB ready: ZentimeOffline database opened successfully');
    });
  }
}

/**
 * Operations that can be performed on offline data
 */
export type OperationType = 'create' | 'update' | 'delete';

/**
 * Interface for operations queued while offline
 */
export interface OfflineOperation {
  id: string;
  table: string;
  operation: OperationType;
  entity_id: string;
  data: any;
  timestamp: string;
  synced: number; // 0: pending, 1: synced, -1: error
  error?: string;
}

/**
 * Interface for caching project access permissions
 */
export interface ProjectAccessCache {
  id: string; // Composite key: userId_projectId
  userId: string;
  projectId: string;
  allowed: boolean;
  checkedAt: number;
  expiresAt: number;
}

/**
 * Creates and exports a singleton instance of the database
 */
export const db = new ZentimeDatabase();

// Store cleanup interval reference
let cleanupIntervalId: NodeJS.Timeout | null = null;

/**
 * Initializes the database and ensures it's ready for use
 * @returns Promise that resolves when the database is ready
 */
export async function initializeOfflineDB(): Promise<boolean> {
  try {
    // Test database connection
    await db.open();
    
    // Register global error handler for database errors - already handled in constructor
    
    logger.store('IndexedDB initialized successfully', {
      version: db.verno,
      tables: Object.keys(db.tables).join(', ')
    });
    
    // Run notifications migration to ensure created_at_ms field exists
    await migrateNotificationsTimestamps();
    
    // Clean up expired project access cache entries
    await clearExpiredProjectAccessCache();
    
    // Clear any existing interval before setting a new one
    if (cleanupIntervalId) {
      clearInterval(cleanupIntervalId);
    }
    
    // Set up periodic cleanup of expired cache entries (every 6 hours - more efficient)
    cleanupIntervalId = setInterval(() => {
      clearExpiredProjectAccessCache().catch(error => 
        logger.error('Failed to clear expired cache in interval:', error)
      );
    }, 6 * 60 * 60 * 1000); // 6 hours instead of 1 hour
    
    return true;
  } catch (error) {
    logger.error('Failed to initialize IndexedDB:', error);
    
    // Return false but don't rethrow to allow app to continue without offline support
    return false;
  }
}

/**
 * Cleans up database resources and intervals
 */
export async function cleanupOfflineDB(): Promise<void> {
  try {
    // Clear the cleanup interval
    if (cleanupIntervalId) {
      clearInterval(cleanupIntervalId);
      cleanupIntervalId = null;
    }
    
    // Close the database connection
    db.close();
    logger.store('IndexedDB cleanup completed');
  } catch (error) {
    logger.error('Failed to cleanup IndexedDB:', error);
  }
}

/**
 * Clears all offline data - useful for logout or troubleshooting
 */
export async function clearOfflineData(): Promise<void> {
  try {
    await db.delete();
    logger.info('IndexedDB data cleared');
  } catch (error) {
    logger.error('Failed to clear IndexedDB data:', error);
  }
}

/**
 * Migrate notifications to add created_at_ms field for reliable IndexedDB sorting
 * 
 * CRITICAL: This migration is ONLY needed for notifications because:
 * 1. Notifications are the ONLY entity that uses IndexedDB's .orderBy() for date sorting
 * 2. IndexedDB cannot reliably sort ISO date strings across browsers
 * 3. Numeric timestamps (created_at_ms) sort perfectly in IndexedDB
 * 4. Other entities sort dates in JavaScript after fetching, so they don't need this
 * 
 * Without this migration, notification ordering would be broken for existing users
 * who have notifications with only the created_at string field.
 */
export async function migrateNotificationsTimestamps(): Promise<boolean> {
  try {
    // Get all notifications that might need the numeric timestamp field
    const notifications = await db.notifications.toArray();
    
    // Add created_at_ms field for IndexedDB sorting performance
    const updates = notifications.map(notification => ({
      ...notification,
      created_at_ms: notification.created_at 
        ? new Date(notification.created_at).getTime() 
        : 0
    }));
    
    // Update notifications with numeric timestamp field
    if (updates.length > 0) {
      await db.notifications.bulkPut(updates);
    }
    
    return true;
  } catch (error) {
    logger.error('Failed to migrate notifications data:', error);
    return false;
  }
}

// Cache duration configuration
const PROJECT_ACCESS_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Cache project access permission for offline use
 */
export async function cacheProjectAccess(
  userId: string,
  projectId: string,
  allowed: boolean
): Promise<void> {
  // Validate inputs
  if (!userId || typeof userId !== 'string') {
    logger.error('Invalid userId provided to cacheProjectAccess');
    return;
  }
  
  if (!projectId || typeof projectId !== 'string') {
    logger.error('Invalid projectId provided to cacheProjectAccess');
    return;
  }
  
  try {
    const now = Date.now();
    const expiresAt = now + PROJECT_ACCESS_CACHE_TTL;
    
    await db.projectAccess.put({
      id: `${userId}_${projectId}`,
      userId,
      projectId,
      allowed,
      checkedAt: now,
      expiresAt
    });
    
    // Only log in development to avoid spamming logs
    if (CONFIG.isDevelopment) {
      logger.store('Cached project access permission', { userId, projectId, allowed });
    }
  } catch (error) {
    logger.error('Failed to cache project access:', error);
  }
}

/**
 * Get cached project access permission
 */
export async function getCachedProjectAccess(
  userId: string,
  projectId: string
): Promise<boolean | null> {
  try {
    const cached = await db.projectAccess.get(`${userId}_${projectId}`);
    
    if (!cached) {
      return null;
    }
    
    // Check if cache is expired
    if (cached.expiresAt < Date.now()) {
      // Remove expired entry
      await db.projectAccess.delete(cached.id);
      return null;
    }
    
    return cached.allowed;
  } catch (error) {
    logger.error('Failed to get cached project access:', error);
    return null;
  }
}

/**
 * Clear expired project access cache entries
 */
export async function clearExpiredProjectAccessCache(): Promise<void> {
  try {
    const now = Date.now();
    const expired = await db.projectAccess
      .where('expiresAt')
      .below(now)
      .toArray();
    
    if (expired.length > 0) {
      await db.projectAccess.bulkDelete(expired.map(e => e.id));
      logger.store(`Cleared ${expired.length} expired project access cache entries`);
    }
  } catch (error) {
    logger.error('Failed to clear expired project access cache:', error);
  }
}

/**
 * Invalidate project access cache for a specific user and optionally a specific project
 * Used when subscription status changes or permissions are updated
 */
export async function invalidateProjectAccessCache(
  userId: string,
  projectId?: string
): Promise<void> {
  try {
    if (projectId) {
      // Invalidate specific project
      await db.projectAccess.delete(`${userId}_${projectId}`);
      logger.store('Invalidated project access cache', { userId, projectId });
    } else {
      // Invalidate all projects for the user
      const userCaches = await db.projectAccess
        .where('userId')
        .equals(userId)
        .toArray();
      
      if (userCaches.length > 0) {
        await db.projectAccess.bulkDelete(userCaches.map(cache => cache.id));
        logger.store(`Invalidated ${userCaches.length} project access cache entries for user`, { userId });
      }
    }
  } catch (error) {
    logger.error('Failed to invalidate project access cache:', error);
  }
}

/**
 * Check if project access is cached and not expired
 */
export async function isProjectAccessCached(
  userId: string,
  projectId: string
): Promise<boolean> {
  try {
    // Check if database is open
    if (!db.isOpen()) {
      await db.open();
    }
    
    const cached = await db.projectAccess.get(`${userId}_${projectId}`);
    return cached ? cached.expiresAt > Date.now() : false;
  } catch (error) {
    // Handle specific IndexedDB errors
    if (error instanceof Error) {
      if (error.name === 'QuotaExceededError') {
        logger.error('IndexedDB quota exceeded when checking project access cache');
      } else if (error.name === 'InvalidStateError') {
        logger.error('IndexedDB in invalid state when checking project access cache');
      } else {
        logger.error('Failed to check project access cache:', error);
      }
    }
    return false;
  }
}